/*
  The rotating card carousel in the FraSyl split hero.

  Dawn's SlideshowComponent is built around the slideshow section's markup contract (slider
  classes, counters, autoplay button) and bending it to this card would cost more than it saves,
  so this is a small self-contained element instead.

  Behaviour: translateX track, dots, prev/next, autoplay that pauses on hover, on focus within,
  and while the tab is hidden. Respects prefers-reduced-motion by not autoplaying at all.
*/
class FrasylHeroCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-hero-track]');
    this.slides = Array.from(this.querySelectorAll('[data-hero-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-hero-dot]'));
    if (!this.track || this.slides.length === 0) return;

    this.index = 0;
    this.interval = parseInt(this.dataset.interval, 10) || 6000;
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.paused = false;

    this.querySelectorAll('[data-hero-prev]').forEach((el) => el.addEventListener('click', () => this.step(-1)));
    this.querySelectorAll('[data-hero-next]').forEach((el) => el.addEventListener('click', () => this.step(1)));
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i)));

    this.addEventListener('mouseenter', () => this.pause());
    this.addEventListener('mouseleave', () => this.resume());
    this.addEventListener('focusin', () => this.pause());
    this.addEventListener('focusout', () => this.resume());

    this.visibilityHandler = () => (document.hidden ? this.pause() : this.resume());
    document.addEventListener('visibilitychange', this.visibilityHandler);

    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.step(-1);
      if (event.key === 'ArrowRight') this.step(1);
    });

    this.render();
    this.restart();
  }

  disconnectedCallback() {
    this.stop();
    document.removeEventListener('visibilitychange', this.visibilityHandler);
  }

  step(direction) {
    this.goTo(this.index + direction);
    // A manual move restarts the dwell so the slide does not vanish immediately -- but it must
    // not un-pause a carousel the pointer is still resting on.
    this.restart();
  }

  goTo(index) {
    const count = this.slides.length;
    this.index = ((index % count) + count) % count;
    this.render();
  }

  render() {
    this.track.style.transform = `translateX(-${this.index * 100}%)`;
    this.slides.forEach((slide, i) => {
      const current = i === this.index;
      // Keep offscreen slides out of the tab order and the accessibility tree.
      slide.setAttribute('aria-hidden', String(!current));
      slide.querySelectorAll('a, button').forEach((el) => {
        if (current) {
          el.removeAttribute('tabindex');
        } else {
          el.setAttribute('tabindex', '-1');
        }
      });
    });
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-on', i === this.index);
      dot.setAttribute('aria-selected', String(i === this.index));
    });
  }

  pause() {
    this.paused = true;
    this.stop();
  }

  resume() {
    this.paused = false;
    this.restart();
  }

  restart() {
    this.stop();
    if (this.paused || this.reduceMotion || this.slides.length < 2 || document.hidden) return;
    this.timer = setInterval(() => this.step(1), this.interval);
  }

  stop() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }
}

customElements.define('frasyl-hero-carousel', FrasylHeroCarousel);
